import React, {CSSProperties, PropsWithChildren, useEffect} from 'react';
import cx from 'classnames';
import styles from './Pages.module.scss'
import Breadcrumb from "react-bootstrap/Breadcrumb";
import {Button, ErrorBoundary} from "fearless-common-ui";
import {ArrowLeft} from "react-bootstrap-icons";

interface BreadcrumbTrailProps {
  breadcrumbs: Array<{ href?: string, label: string }>
}

function BreadcrumbTrail({ breadcrumbs }: BreadcrumbTrailProps) {
    return (
        <Breadcrumb className="mb-0" listProps={{ className: 'mb-0' }}>
            {breadcrumbs.map(({ href, label }) => (
                <Breadcrumb.Item
                  className={href ? undefined : 'text-decoration-none'}
                  key={label || href}
                  href={href}
                  linkAs={href ? 'a' : 'span'}
                >
                    {label}
                </Breadcrumb.Item>
            ))}
        </Breadcrumb>
    );
}


function Page({ children, padded, className = '' }: PropsWithChildren<{ padded?: boolean; className?: string }>) {
    useEffect(() => {
        window.scrollTo(0, 0)
    }, []);

    return <div className={cx({
        [styles.Page]: true,
        'pt-5': padded,
        [className]: Boolean(className),
    })}>{children}</div>
}

const headerClassName = 'bg-primary py-4 px-5 text-white h4';
function UserBase({ children, padded = true, header }: PropsWithChildren<{
  padded?: boolean;
  header?: string | React.JSX.Element | { href?: string; label: string }[];
}>) {
    useEffect(() => {
        window.scrollTo(0, 0)
    }, []);

    return <div className={cx({[styles.PageBase]: true,})}>
        {header && !Array.isArray(header) && <h1 className={headerClassName}>{header}</h1>}
        {Array.isArray(header) && (
            <Breadcrumb className={`${headerClassName} ${styles.HeaderBreadcrumb}`} >
                {header.map((headerItem) => (
                    <Breadcrumb.Item
                        key={headerItem.label}
                        className={cx({[styles.HeaderBreadcrumbItem]: true, [styles.NoHref]: !headerItem.href })}
                        href={headerItem.href}
                    >
                        {headerItem.label}
                    </Breadcrumb.Item>
                ))}
            </Breadcrumb>
        )}
        <div className={padded ? "pt-4 px-5" : ''}>
            {children}
        </div>

    </div>
}

function HeaderPage({ children, className = '', header, bgColorClassName = '' }: PropsWithChildren<{
  className?: string;
  bgColorClassName?: string;
  header?: string | React.JSX.Element;
}>) {
    return (
        <Page padded={false}>
            <div
                className={cx({
                    [className]: !!className
                })}
            >
                <div className={`${bgColorClassName} py-5`}>{header}</div>
                {children}
            </div>
        </Page>
    );
}

function CentredPage({ children, padded = true, width = '', className = '', breadcrumbs, backButton }: PropsWithChildren<{
  width?: CSSProperties['width'];
  className?: string;
  padded?: boolean;
  breadcrumbs?: BreadcrumbTrailProps['breadcrumbs'];
  backButton?: {
    label: string;
    href: string;
  },
}>) {
    return (
        <Page padded={padded}>
          <div
              className={cx({
                  'container-sm px-3': width === 'sm',
                  'container-xs px-3': width === 'xs',
                  'container': !width,
                  [className]: !!className
              })}
          >
            <ErrorBoundary>
              {(backButton || breadcrumbs) && (
                <div className="d-flex align-items-center mb-1 gap-3">
                  {backButton && (
                    <BackButton href={backButton.href}>{backButton.label}</BackButton>
                  )}
                  {breadcrumbs && <BreadcrumbTrail breadcrumbs={breadcrumbs} />}
                </div>
              )}
              {children}
            </ErrorBoundary>
          </div>
        </Page>
    );
}

function BackButton({ children, href }: PropsWithChildren<{ href: string }>) {
  return (
    <Button variant="outline-primary" size="sm" href={href}><ArrowLeft /> back to {children}</Button>
  );
}

const Pages = {
  Centred: CentredPage,
  Base: Page,
  UserBase: UserBase,
  HeaderPage: HeaderPage,
  BackButton: BackButton,
}

export default Pages
